<script setup lang="ts">
import { LineChart } from 'vue-chart-3';
import { computed, ref } from 'vue';
import 'chart.js/auto';
import 'chartjs-adapter-date-fns';

interface Props {
    data: Record<string, number | null>;
    question: string|null;
    questionType: string;
}

const props = defineProps<Props>();

// Get the most recent date from the data to set as default end date
const getLatestDate = () => {
    const dates = Object.keys(props.data).filter(date => props.data[date] !== null);
    if (dates.length === 0) return new Date().toISOString().split('T')[0];
    return dates.sort().pop() || new Date().toISOString().split('T')[0];
};

const endDate = ref(getLatestDate());

const moveWeek = (direction: number) => {
    const current = new Date(endDate.value);
    current.setDate(current.getDate() + (direction * 7));
    endDate.value = current.toISOString().split('T')[0];
};

const canMoveForward = computed(() => {
    const dates = Object.keys(props.data).filter(date => props.data[date] !== null);
    if (dates.length === 0) return false;
    const latestDataDate = dates.sort().pop();
    return latestDataDate ? endDate.value < latestDataDate : false;
});

const canMoveBackward = computed(() => {
    const dates = Object.keys(props.data).filter(date => props.data[date] !== null);
    if (dates.length === 0) return false;
    const earliestDataDate = dates.sort()[0];
    if (!earliestDataDate) return false;

    const thirtyDaysAgo = new Date(endDate.value);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    return thirtyDaysAgo.toISOString().split('T')[0] > earliestDataDate;
});

// Calculate min/max values from entire dataset
const datasetBounds = computed(() => {
    const values = Object.values(props.data).filter(v => v !== null) as number[];
    const isBinaryType = props.questionType === 'boolean' || props.questionType === 'todo';

    if (values.length === 0) return { min: 0, max: isBinaryType ? 1 : 100, isBinaryType };

    if (isBinaryType) {
        return {
            min: 0,
            max: 1,
            isBinaryType: true
        };
    }

    const min = Math.min(...values);
    const max = Math.max(...values);

    // Add some padding (10% of range)
    const range = max - min;
    const padding = range * 0.1;

    return {
        min: Math.max(0, min - padding),
        max: max + padding,
        isBinaryType: false
    };
});

const chartData = computed(() => {
    if (!props.data || Object.keys(props.data).length === 0) {
        return { labels: [], datasets: [] };
    }

    // Calculate 30-day window
    const end = new Date(endDate.value);
    const start = new Date(end);
    start.setDate(start.getDate() - 30);

    const startDateString = start.toISOString().split('T')[0];
    const endDateString = end.toISOString().split('T')[0];

    // Generate all dates in the 30-day window
    const allDates = [];
    const currentDate = new Date(start);
    while (currentDate <= end) {
        allDates.push(currentDate.toISOString().split('T')[0]);
        currentDate.setDate(currentDate.getDate() + 1);
    }

    // Create data array with actual values or null for missing days
    const labels = allDates;
    const values = allDates.map(date => {
        const value = props.data[date];
        if (value === null) return null;

        // For todo type, convert any non-zero value to 1, zero stays 0
        if (props.questionType === 'todo') {
            return value !== 0 ? 1 : 0;
        }

        return value;
    });

    return {
        labels: labels,
        datasets: [
            {
                label: props.question || '',
                data: values,
                backgroundColor: 'rgba(120, 120, 120, 1)',
                borderColor: 'rgba(120, 120, 120, 1)',
                borderWidth: 2,
                tension: 0.2,
                pointRadius: 4,
                pointHoverRadius: 6,
                fill: false,
                spanGaps: false, // Don't connect lines across null values
            },
        ],
    };
});

const chartOptions = computed(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            display: false,
        },
        tooltip: {
            mode: 'index' as const,
            intersect: false,
        },
    },
    interaction: {
        mode: 'nearest' as const,
        axis: 'x' as const,
        intersect: false,
    },
    scales: {
        x: {
            type: 'time' as const,
            time: {
                unit: 'day' as const,
            },
            title: {
                display: false,
                text: 'Date',
            },
        },
        y: {
            beginAtZero: false,
            min: datasetBounds.value.min,
            max: datasetBounds.value.max,
            afterDataLimits: (scale) => {
                scale.max += (scale.max - scale.min) * 0.02;
            },
            title: {
                display: false,
                text: 'Value',
            },
            ...(datasetBounds.value.isBinaryType && {
                ticks: {
                    stepSize: 1,
                    callback: function(value: any) {
                        if (value === 0) return 'No';
                        if (value === 1) return 'Yes';
                        return value;
                    }
                }
            }),
        },
    },
}));
</script>

<template>
    <div>
        <div v-if="chartData.datasets && chartData.datasets.length > 0">
            <LineChart :key="endDate" :options="chartOptions" :chart-data="chartData" />
            <div class="flex justify-center space-x-2 mt-2">
                <button-base
                    @click="canMoveBackward ? moveWeek(-1) : null"
                    :disabled="!canMoveBackward"
                    class="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-300 rounded"
                >
                    ← 7 {{ _mft('shared:days').toLowerCase() }}
                </button-base>
                <button-base
                    @click="canMoveForward ? moveWeek(1) : null"
                    :disabled="!canMoveForward"
                    class="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-300 rounded"
                >
                    7 {{ _mft('shared:days').toLowerCase() }} →
                </button-base>
            </div>
        </div>
        <div v-else class="text-center text-gray-500 py-8">
            No data available for this period
        </div>
    </div>
</template>

<style scoped>
/* Additional styling if needed */
</style>
